import { storableError } from '../../../../util/errors';
import { fetchCurrentUser } from '../../../../ducks/user.duck';
import { addMarketplaceEntities } from '../../../../ducks/marketplaceData.duck';
import { apiBaseUrl } from '../../../../util/api';
import { getFromIndexedDB, saveToIndexedDB, deleteFromIndexedDB } from '../../../../util/indexedDBCache';

// ================ Cache Configuration ================ //

const CACHE_KEY = 'landingPageMapListings';
const CACHE_TIMESTAMP_KEY = 'landingPageMapListingsTimestamp';
const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes

// ================ Action types ================ //

export const FETCH_MAP_LISTINGS_REQUEST = 'app/LandingPageMap/FETCH_MAP_LISTINGS_REQUEST';
export const FETCH_MAP_LISTINGS_SUCCESS = 'app/LandingPageMap/FETCH_MAP_LISTINGS_SUCCESS';
export const FETCH_MAP_LISTINGS_ERROR = 'app/LandingPageMap/FETCH_MAP_LISTINGS_ERROR';
export const LOAD_CACHED_MAP_LISTINGS = 'app/LandingPageMap/LOAD_CACHED_MAP_LISTINGS';

// ================ Reducer ================ //

const initialState = {
  listingIds: [],
  fetchInProgress: false,
  fetchError: null,
  lastFetchTimestamp: null,
  isFromCache: false,
  cacheSource: null, // 'browser', 'server', or null
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_MAP_LISTINGS_REQUEST:
      // Don't clear listingIds - allows stale pins to show while loading (better UX than blank map)
      return { ...state, fetchInProgress: true, fetchError: null, isFromCache: false };
    case FETCH_MAP_LISTINGS_SUCCESS:
      console.log('🔴 REDUCER: FETCH_MAP_LISTINGS_SUCCESS', {
        listingIdsCount: payload.listingIds?.length,
        timestamp: payload.timestamp,
        isFromCache: payload.isFromCache,
        cacheSource: payload.cacheSource
      });
      return { 
        ...state, 
        listingIds: payload.listingIds, 
        fetchInProgress: false,
        lastFetchTimestamp: payload.timestamp,
        isFromCache: payload.isFromCache || false,
        cacheSource: payload.cacheSource || null,
      };
    case LOAD_CACHED_MAP_LISTINGS:
      console.log('🔴 REDUCER: LOAD_CACHED_MAP_LISTINGS', {
        listingIdsCount: payload.listingIds?.length,
        timestamp: payload.timestamp,
        cacheSource: payload.cacheSource
      });
      return {
        ...state,
        listingIds: payload.listingIds,
        lastFetchTimestamp: payload.timestamp,
        fetchInProgress: false,
        isFromCache: true,
        cacheSource: payload.cacheSource,
      };
    case FETCH_MAP_LISTINGS_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    default:
      return state;
  }
}

// ================ Cache Helpers ================ //

const getCachedListings = async () => {
  if (typeof window === 'undefined') return null;
  
  // Try IndexedDB first (can handle large datasets)
  try {
    console.log('🔍 Checking IndexedDB cache...');
    const cached = await getFromIndexedDB(CACHE_KEY);
    
    if (!cached) {
      console.log('❌ No IndexedDB cache found, trying localStorage fallback...');
      // Fall back to localStorage for backwards compatibility
      return getCachedListingsFromLocalStorage();
    }
    
    const age = Date.now() - cached.timestamp;
    if (age > CACHE_DURATION_MS) {
      console.log(`❌ IndexedDB cache expired, age: ${Math.round(age / 1000 / 60)} minutes`);
      await deleteFromIndexedDB(CACHE_KEY);
      return null;
    }
    
    // Fix deserialized UUIDs - convert plain objects OR strings back to UUID instances
    if (cached.listingIds && Array.isArray(cached.listingIds)) {
      const { UUID } = require('sharetribe-flex-sdk').types;
      cached.listingIds = cached.listingIds.map(id => {
        // If it's already a UUID instance, return it
        if (id && id._sdkType === 'UUID') {
          return id;
        }
        // If it's a string UUID, convert to UUID instance
        if (typeof id === 'string') {
          return new UUID(id);
        }
        // If it's a plain object with uuid property, convert to UUID instance  
        if (id && typeof id === 'object' && id.uuid) {
          return new UUID(id.uuid);
        }
        return id;
      });
      console.log('✅ Reconstituted UUIDs from IndexedDB cache');
    }
    
    console.log(`✅ Loaded ${cached.listingIds?.length || 0} listings from IndexedDB (age: ${Math.round(age / 1000 / 60)}min)`);
    return cached;
  } catch (e) {
    console.error('❌ Error reading IndexedDB cache:', e);
    return getCachedListingsFromLocalStorage();
  }
};

const getCachedListingsFromLocalStorage = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (!cached || !timestamp) {
      console.log('❌ No localStorage cache found');
      return null;
    }
    
    const age = Date.now() - parseInt(timestamp, 10);
    if (age > CACHE_DURATION_MS) {
      console.log(`❌ LocalStorage cache expired, age: ${Math.round(age / 1000 / 60)} minutes`);
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      return null;
    }
    
    const data = JSON.parse(cached);
    
    // Fix deserialized UUIDs - convert plain objects OR strings back to UUID instances
    if (data.listingIds && Array.isArray(data.listingIds)) {
      const { UUID } = require('sharetribe-flex-sdk').types;
      data.listingIds = data.listingIds.map(id => {
        // If it's already a UUID instance, return it
        if (id && id._sdkType === 'UUID') {
          return id;
        }
        // If it's a string UUID, convert to UUID instance
        if (typeof id === 'string') {
          return new UUID(id);
        }
        // If it's a plain object with uuid property, convert to UUID instance
        if (id && typeof id === 'object' && id.uuid) {
          return new UUID(id.uuid);
        }
        return id;
      });
      console.log('✅ Reconstituted UUIDs from localStorage cache');
    }
    
    console.log(`✅ Loaded ${data.listingIds?.length || 0} listings from localStorage (age: ${Math.round(age / 1000 / 60)}min)`);
    return data;
  } catch (e) {
    console.error('❌ Error reading localStorage cache:', e);
    return null;
  }
};

const setCachedListings = async (listingIds, entities) => {
  if (typeof window === 'undefined') return;
  
  const timestamp = Date.now();
  const data = {
    listingIds,
    entities,
    timestamp,
  };
  
  // Try IndexedDB first (can handle large datasets)
  try {
    const jsonString = JSON.stringify(data);
    const sizeMB = (jsonString.length / 1024 / 1024).toFixed(2);
    console.log(`Caching ${listingIds.length} listings with ${entities?.data?.length || 0} data items and ${entities?.included?.length || 0} included items...`);
    console.log(`Attempting to cache ${listingIds.length} listings (${sizeMB} MB) to IndexedDB...`);
    
    const success = await saveToIndexedDB(CACHE_KEY, data);
    if (success) {
      console.log(`✅ Cached ${listingIds.length} listings successfully in IndexedDB!`);
      // Also clear old localStorage cache
      try {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      } catch (e) {
        // Ignore localStorage errors
      }
      return;
    }
  } catch (e) {
    console.error('❌ Error writing to IndexedDB:', e);
  }
  
  // Fallback: Try localStorage with IDs only for backwards compatibility
  console.warn('⚠️ IndexedDB failed, falling back to localStorage with IDs only...');
  try {
    const minimalData = {
      listingIds,
      entities: null,
      timestamp,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(minimalData));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, timestamp.toString());
    console.log(`✓ Cached ${listingIds.length} listing IDs only in localStorage (fallback)`);
  } catch (e2) {
    console.error('❌ Failed to cache even to localStorage:', e2);
  }
};

// ================ Action creators ================ //

export const fetchMapListingsRequest = () => ({ type: FETCH_MAP_LISTINGS_REQUEST });
export const fetchMapListingsSuccess = (listingIds, timestamp, isFromCache = false, cacheSource = null) => ({
  type: FETCH_MAP_LISTINGS_SUCCESS,
  payload: { listingIds, timestamp: timestamp || Date.now(), isFromCache, cacheSource },
});
export const loadCachedMapListings = (listingIds, timestamp, cacheSource) => ({
  type: LOAD_CACHED_MAP_LISTINGS,
  payload: { listingIds, timestamp, cacheSource },
});
export const fetchMapListingsError = error => ({
  type: FETCH_MAP_LISTINGS_ERROR,
  payload: error,
  error: true,
});

// ================ Thunks ================ //

/**
 * Progressive phase-based loading for faster map display
 * Phase 1: Show map in 2-3 seconds with first ~200 listings
 * Phase 2: Add more listings in 5-7 seconds
 * Phase 3: Complete all listings in 10-15 seconds
 */
export const fetchMapListings = (forceRefresh = false) => async (dispatch, getState, sdk) => {
  // Check browser cache FIRST (instant load for returning visitors from IndexedDB)
  if (!forceRefresh) {
    const cached = await getCachedListings();
    if (cached) {
      if (cached.entities) {
        // Full cache with entities - load immediately and done!
        console.log('✅ Cache has full entities - instant display!');
        // Wrap entities in SDK format: { data: entities }
        dispatch(addMarketplaceEntities({ data: cached.entities }));
        dispatch(loadCachedMapListings(cached.listingIds, cached.timestamp, 'indexeddb'));
        return Promise.resolve(cached.listingIds);
      } else {
        // ID-only cache (localStorage fallback) - dispatch IDs immediately, fetch entities in background
        console.log('⚠️ Cache has IDs only - displaying IDs now, fetching entities...');
        dispatch(loadCachedMapListings(cached.listingIds, cached.timestamp, 'localstorage-ids-only'));
        
        // Fetch full entities from server API in background to update with complete data
        fetchPhaseFromServerAPI(dispatch, 'phase1', forceRefresh)
          .then(() => fetchPhaseFromServerAPI(dispatch, 'phase2', forceRefresh))
          .then(() => fetchPhaseFromServerAPI(dispatch, 'phase3', forceRefresh))
          .catch(error => {
            console.error('Server API failed, falling back to SDK:', error);
            return fetchFromSharetribeSDK(dispatch, sdk);
          });
        
        return Promise.resolve(cached.listingIds);
      }
    }
  }
  
  // No cache available - show loading state and fetch progressively
  dispatch(fetchMapListingsRequest());
  
  // Fetch Phase 1 ASAP (quick load, shows map)
  let allListingIds = [];
  try {
    const phase1Ids = await fetchPhaseFromServerAPI(dispatch, 'phase1', forceRefresh);
    allListingIds = [...allListingIds, ...phase1Ids];
    console.log(`📍 Phase 1 complete: ${allListingIds.length} listings visible on map`);
    
    // Fetch Phase 2 in background (add more listings)
    setTimeout(() => {
      fetchPhaseFromServerAPI(dispatch, 'phase2', forceRefresh)
        .then(phase2Ids => {
          allListingIds = [...allListingIds, ...phase2Ids];
          console.log(`📍 Phase 2 complete: ${allListingIds.length} listings visible on map`);
          
          // Fetch Phase 3 (complete all listings)
          return fetchPhaseFromServerAPI(dispatch, 'phase3', forceRefresh);
        })
        .then(phase3Ids => {
          allListingIds = [...allListingIds, ...phase3Ids];
          console.log(`✅ All phases complete: ${allListingIds.length} total listings on map`);
        })
        .catch(error => {
          console.error('Phase 2/3 failed, map still works with phase 1:', error);
        });
    }, 100); // Very small delay between phases
    
    return Promise.resolve(allListingIds);
  } catch (error) {
    console.error('Phase 1 failed, trying SDK fallback:', error);
    // Fallback: Direct Sharetribe SDK call (old behavior)
    return fetchFromSharetribeSDK(dispatch, sdk);
  }
};

/**
 * Fetch a specific phase from server API (phase1, phase2, or phase3)
 * Progressively renders listings as each phase completes
 */
const fetchPhaseFromServerAPI = async (dispatch, phaseName, forceRefresh = false) => {
  try {
    const baseUrl = apiBaseUrl();
    const url = `${baseUrl}/api/map-listings?phase=${phaseName}&forceRefresh=${forceRefresh}`;
    console.log(`🚀 Fetching ${phaseName} from server API...`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Server API returned ${response.status}`);
    }
    
    const result = await response.json();
    const { data, phase, cached, totalListings, duration } = result;
    const { listingIds, entities } = data;
    
    // Add entities to Redux store
    if (entities) {
      dispatch(addMarketplaceEntities({ data: entities }));
    }
    
    // Update Redux state with this phase's listings
    // This triggers immediate map update without waiting for all phases
    const timestamp = Date.now();
    dispatch(fetchMapListingsSuccess(listingIds, timestamp, cached, `server-${phase}`));
    
    // Also update browser cache incrementally
    const currentCached = await getCachedListings();
    const mergedIds = [...(currentCached?.listingIds || []), ...listingIds];
    const mergedEntities = {
      data: [...(currentCached?.entities?.data || []), ...(entities?.data || [])],
      included: [...(currentCached?.entities?.included || []), ...(entities?.included || [])],
    };
    await setCachedListings(mergedIds, mergedEntities);
    
    const logMsg = `✓ ${phase} loaded: ${totalListings} new listings ${cached ? '(cached)' : `(fresh, ${duration}s)`}`;
    console.log(logMsg);
    
    return listingIds;
  } catch (error) {
    console.error(`Error fetching ${phaseName} from server API:`, error);
    throw error;
  }
};

/**
 * OLD: Fetch from server API endpoint (kept for reference, replaced by phase-based)
 */
const fetchFromServerAPILegacy = async (dispatch, forceRefresh = false) => {
  try {
    const baseUrl = apiBaseUrl();
    const url = `${baseUrl}/api/map-listings?forceRefresh=${forceRefresh}`;
    console.log(`🔄 Fetching from server API: ${url}`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Server API returned ${response.status}`);
    }
    
    const result = await response.json();
    const { data, cached, cacheAge } = result;
    const { listingIds, entities } = data;
    
    // Add entities to Redux store
    // entities has { data: [...], included: [...] }
    // But addMarketplaceEntities expects SDK format: { data: { data: [...], included: [...] } }
    if (entities) {
      dispatch(addMarketplaceEntities({ data: entities }));
    }
    
    // Update Redux state
    const timestamp = Date.now();
    dispatch(fetchMapListingsSuccess(listingIds, timestamp, cached, 'server'));
    
    // Update browser cache for instant future loads (IndexedDB can handle large data)
    await setCachedListings(listingIds, entities);
    
    console.log(`✓ Loaded ${listingIds.length} listings from server ${cached ? '(cached' : '(fresh'}, age: ${Math.round(cacheAge / 1000 / 60)}min)`);
    
    return listingIds;
  } catch (error) {
    console.error('Error fetching from server API:', error);
    throw error;
  }
};

/**
 * Fallback: Fetch directly from Sharetribe SDK (old behavior)
 */
// TOL Level 2 industry codes for multi-query strategy
const TOL_LEVEL_2_CODES = [
  '01', '02', '03', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', 
  '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', 
  '30', '31', '32', '33', '35', '36', '37', '38', '39', '41', '42', '43', '45', '46', 
  '47', '49', '50', '51', '52', '53', '55', '56', '58', '59', '60', '61', '62', '63', 
  '64', '65', '66', '68', '69', '70', '71', '72', '73', '74', '75', '77', '78', '79', 
  '80', '81', '82', '84', '85', '86', '87', '88', '90', '91', '92', '93', '94', '95', 
  '96', '97', '98', '99', '00'
];

const fetchFromSharetribeSDK = async (dispatch, sdk) => {
  console.log('🚀 SDK Fallback: Using multi-query strategy to bypass 10,000 limit...');
  
  // Fetch listings progressively by industry segments
  let allListingIds = [];
  let allEntities = [];
  let processedSegments = 0;
  
  for (const industryCode of TOL_LEVEL_2_CODES) {
    try {
      // Fetch up to 100 pages (10,000) per industry
      let industryListings = [];
      let industryEntities = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore && page <= 100) {
        const response = await sdk.listings.query({
          include: ['images'],
          'fields.listing': ['title', 'geolocation', 'price', 'publicData.mainLocation', 'publicData.industryCodeLevel2'],
          'fields.image': ['variants.scaled-small', 'variants.scaled-medium'],
          pub_mainLocation: true,
          pub_industryCodeLevel2: industryCode,
          per_page: 100,
          page: page,
          state: 'published',
        });
        
        const listings = response.data.data;
        const meta = response.data.meta;
        
        // Add entities to Redux store progressively
        dispatch(addMarketplaceEntities(response));
        
        // Collect for this segment
        industryEntities.push(response.data);
        industryListings = [...industryListings, ...listings];
        
        // Check if more pages exist
        hasMore = page < meta.totalPages && page < 100;
        page++;
        
        if (page > 100 && meta.totalPages > 100) {
          console.warn(`⚠️ TOL-${industryCode}: Hit 100-page limit (${meta.totalItems} total)`);
        }
      }
      
      // Filter for geolocation and deduplicate
      const validListings = industryListings.filter(listing => 
        listing.attributes.geolocation && !allListingIds.some(id => id.uuid === listing.id.uuid)
      );
      const newIds = validListings.map(listing => listing.id);
      
      allListingIds = [...allListingIds, ...newIds];
      allEntities = [...allEntities, ...industryEntities];
      
      processedSegments++;
      
      // Update map progressively every 5 segments
      if (processedSegments % 5 === 0 || processedSegments === TOL_LEVEL_2_CODES.length) {
        const timestamp = Date.now();
        dispatch(fetchMapListingsSuccess(allListingIds, timestamp, false, 'sdk-fallback-multi'));
        console.log(`📊 Processed ${processedSegments}/${TOL_LEVEL_2_CODES.length} industry segments, ${allListingIds.length} listings on map`);
      }
      
    } catch (error) {
      console.error(`❌ Error fetching industry ${industryCode}:`, error.message);
      // Continue with next segment
    }
  }
  
  // Cache final result
  console.log(`✅ Multi-query complete: ${allListingIds.length} total listings fetched!`);
  
  try {
    const mergedEntities = {
      data: allEntities.flatMap(e => e.data || []),
      included: allEntities.flatMap(e => e.included || []),
    };
    
    console.log(`Caching ${allListingIds.length} listings with ${mergedEntities.data.length} data items and ${mergedEntities.included.length} included items...`);
    await setCachedListings(allListingIds, mergedEntities);
    console.log('✓ Cache save completed successfully!');
  } catch (error) {
    console.error('❌ Error saving cache:', error);
  }
  
  return allListingIds;
};

/**
 * Fetch remaining pages of main locations only
 */
const fetchRemainingPagesMainLocations = async (sdk, dispatch, startPage, existingListingIds, totalPages, allEntities = []) => {
  if (startPage > totalPages) {
    console.log(`✓ All ${totalPages} pages fetched (main locations), total on map: ${existingListingIds.length}`);
    
    // Cache the complete result
    const mergedEntities = {
      data: allEntities.flatMap(e => e.data || []),
      included: allEntities.flatMap(e => e.included || []),
    };
    await setCachedListings(existingListingIds, mergedEntities);
    
    return;
  }
  
  sdk.listings
    .query({
      include: ['images'],
      'fields.listing': ['title', 'geolocation', 'price', 'publicData.mainLocation'],
      'fields.image': ['variants.scaled-small', 'variants.scaled-medium'],
      pub_mainLocation: true,
      per_page: 100,
      page: startPage,
      state: 'published',
    })
    .then(response => {
      const listings = response.data.data;
      
      // Add entities to Redux store
      dispatch(addMarketplaceEntities(response));
      
      // Collect entities for caching
      allEntities.push(response.data);
      
      // Filter for listings with geolocation
      const validListings = listings.filter(listing => listing.attributes.geolocation);
      const newListingIds = validListings.map(listing => listing.id);
      
      // Combine with existing listing IDs
      const allListingIds = [...existingListingIds, ...newListingIds];
      
      // Update the map with new listings
      dispatch(fetchMapListingsSuccess(allListingIds, Date.now()));
      
      console.log(`Fetched page ${startPage}/${totalPages} (main locations), total on map: ${allListingIds.length}`);
      
      // Continue with next page
      fetchRemainingPagesMainLocations(sdk, dispatch, startPage + 1, allListingIds, totalPages, allEntities);
    })
    .catch(e => {
      console.error(`Error fetching page ${startPage}:`, e);
      // Continue despite errors
      fetchRemainingPagesMainLocations(sdk, dispatch, startPage + 1, existingListingIds, totalPages, allEntities);
    });
};
